import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { trpc, TrpcOutputs } from '../config/trpc';
import { RouteEditorLegForm, SegmentsFormValues } from './RouteEditorSegments';
import { SimpleForm } from './SimpleForm';

type Props = UseDisclosureReturn & {
  legs?: TrpcOutputs['updateRoutes'];
};

export const RouteEditorSegmentModal = ({ legs, ...disclosure }: Props) => {
  const { mutateAsync: bulkModifyFlightSegments, isLoading: isUpdatingSegments } =
    trpc.bulkModifyFlightSegments.useMutation();

  const segmentsForm = useForm<SegmentsFormValues>({ defaultValues: { legs } });
  const { reset, formState } = segmentsForm;

  useEffect(() => {
    if (legs?.length) reset({ legs });
  }, [legs, reset]);

  const onValid = async (values: SegmentsFormValues) => {
    const dirtyFields = formState.dirtyFields;

    const dirtySegments = Object.keys(dirtyFields.legs || {}).flatMap((legIndex) => {
      const legDirtyFields = dirtyFields.legs[legIndex]?.segments || {};
      return Object.keys(legDirtyFields)
        .filter((segmentIndex) => Object.values(legDirtyFields[segmentIndex]).some((value) => value === true))
        .map((segmentIndex) => values.legs[legIndex].segments[segmentIndex]);
    });

    await bulkModifyFlightSegments({ flight_segments: dirtySegments, preserve_history: false });
    disclosure.onClose();
  };

  if (!legs?.length) return null;

  return (
    <Modal {...disclosure} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading>Configure New Segments</Heading>
          <Text>Some flight segments didn't exist and segments have been created. Please fill in these values:</Text>
        </ModalHeader>
        <SimpleForm {...segmentsForm}>
          <form onSubmit={segmentsForm.handleSubmit(onValid)}>
            <ModalBody overflowX="auto">
              <RouteEditorLegForm />
            </ModalBody>
            <ModalFooter>
              <Button type="submit" colorScheme="primary" isLoading={isUpdatingSegments}>
                Save
              </Button>
            </ModalFooter>
          </form>
        </SimpleForm>
      </ModalContent>
    </Modal>
  );
};
